import { Flex, Link } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDialog } from 'components/common/dialogs';
import { CommonFileInput } from 'components/common/form/file';
import { CommonProgress } from 'components/common/progress';
import { t } from 'i18next';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { BYTES_PER_MB, TextMime, VideoMime } from 'lib_ts/enums/files.enums';
import { DeliveryType, PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { CameraPosition } from 'lib_ts/enums/videos.enums';
import { VIDEO_CSV_FLOATS, VIDEO_CSV_STRINGS } from 'lib_ts/interfaces/i-video';
import { clamp, first } from 'lodash';
import { useCallback, useState } from 'react';
import { VideosService } from 'services/videos.service';
import { useVideosStore } from 'stores/use-videos-store';

const MAX_VIDEO_SIZE_MB = 50;

export const VideoUploadDialog = (props: {
  static: boolean;
  onClose: () => void;
}) => {
  const uploadVideos = useCallback(
    async (files: File[], onProgress?: (ev: ProgressEvent) => void) => {
      try {
        /** append the files */
        const formData = new FormData();
        files.forEach((f) => {
          formData.append('videos', f);
        });

        useVideosStore.setState({ loading: true });

        const result = await VideosService.getInstance()
          .createMany(props.static, formData, onProgress)
          .finally(() => useVideosStore.setState({ loading: false }));

        const processed = result.reports.filter((f) => !f.skipped);

        if (processed.length > 0) {
          NotifyHelper.success({
            message_md:
              processed.length > 1
                ? `Successfully uploaded ${processed.length} videos!`
                : 'Successfully uploaded one video!',
          });
        }

        const skipped = result.reports.filter((f) => f.skipped);

        if (skipped.length > 0) {
          console.warn({
            event: `Skipped ${skipped.length} video file(s)`,
            skipped,
          });

          NotifyHelper.warning({
            message_md: `${skipped.length} ${
              skipped.length === 1 ? 'video' : 'videos'
            } could not be processed, see console for details.`,
          });
        }

        /** triggers context to refresh videos list */
        useVideosStore.getState().refresh();

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      }
    },
    [props.static]
  );

  const [csvFile, setCsvFile] = useState<File>();
  const [videoFiles, setVideoFiles] = useState<File[]>();

  const [progress, setProgress] = useState<{
    label: string;
    loadedPercent: number; // 0-100
  }>();

  const handleProgressEvent = (ev: ProgressEvent) => {
    const loadedPercent =
      ev.total === 0 ? 0 : clamp(ev.loaded / ev.total, 0, 1) * 100;

    const label =
      loadedPercent < 100
        ? `${loadedPercent.toFixed(0)}%`
        : 'Upload complete, processing...';

    setProgress({
      label: label,
      loadedPercent: loadedPercent,
    });
  };

  return (
    <CommonDialog
      // while an upload is in progress, avoid dismissing dialog by accident
      modal={progress && progress.loadedPercent !== 100}
      identifier="VideoUploadDialog"
      width={RADIX.DIALOG.WIDTH.SM}
      title="videos.upload-videos"
      content={
        <Flex direction="column" gap="3">
          <CommonFileInput
            id="VideoUploadVideoFiles"
            label="videos.videos"
            placeholder="videos.upload-one-or-multiple-mp4-files"
            acceptedTypes={[VideoMime.MP4]}
            filename={
              videoFiles ? `${videoFiles.length} file(s) selected` : undefined
            }
            onChange={async (files: File[]) => {
              const anyFileTooLarge = files.find(
                (f) => f.size > BYTES_PER_MB * MAX_VIDEO_SIZE_MB
              );

              if (anyFileTooLarge) {
                NotifyHelper.warning({
                  message_md: `Max file size (${MAX_VIDEO_SIZE_MB} MB) exceeded.`,
                });
                return;
              }

              setVideoFiles(files);
            }}
            disabled={!!progress}
            multiple
          />

          {!props.static && (
            <CommonFileInput
              id="VideoUploadCsvFile"
              label="videos.metadata-file-optional"
              placeholder="videos.upload-a-csv-file"
              acceptedTypes={[TextMime.CSV]}
              filename={csvFile?.name}
              onChange={async (files: File[]) => setCsvFile(first(files))}
              disabled={!!progress}
            />
          )}

          <Link
            className="cursor-pointer"
            onClick={() => {
              const csvCols = [...VIDEO_CSV_STRINGS, ...VIDEO_CSV_FLOATS];

              const sampleRow1 = [
                'ABCD-1234',
                'video-file-1.mp4',
                'First Video',
                'John Pitcher',
                'Wildcats',
                PitchType.FourSeamFastball,
                DeliveryType.Windup,
                CameraPosition.RHH,
                100,
                200,
                10,
                20,
                1.5,
                3,
                4,
              ];

              const sampleRow2 = [
                'ABCD-5678',
                'video-file-2.mp4',
                'Second Video',
                'Michael Pitcher',
                'Wildcats',
                PitchType.Curveball,
                DeliveryType.Stretch,
                CameraPosition.LHH,
                100,
                200,
                10,
                20,
                1.5,
                3,
                4,
              ];

              const csvString = [
                csvCols.join(','),
                sampleRow1.join(','),
                sampleRow2.join(','),
              ].join('\n');

              const blob = new Blob([csvString], { type: 'text/csv' });
              MiscHelper.saveAs(blob, 'sample-import-videos.csv');
            }}
          >
            {t('videos.download-a-sample-csv-file')}
          </Link>

          {progress && (
            <CommonProgress
              value={progress.loadedPercent}
              rightLabel={progress.label}
            />
          )}
        </Flex>
      }
      buttons={[
        {
          label: progress ? 'common.uploading' : 'common.import',
          color: RADIX.COLOR.ACCENT,
          variant: 'solid',
          disabled: !!progress || !videoFiles || videoFiles.length === 0,
          onClick: () => {
            if (!videoFiles) {
              return;
            }

            const safeFiles = csvFile ? [...videoFiles, csvFile] : videoFiles;

            // upload the file to the server and get the records upserted
            uploadVideos(safeFiles, handleProgressEvent).finally(() => {
              setCsvFile(undefined);
              setVideoFiles(undefined);

              // clear old progress object after a delay
              setTimeout(() => {
                setProgress(undefined);
              }, 2_000);
            });
          },
        },
      ]}
      onClose={props.onClose}
    />
  );
};
